import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, ArrowRight, Check, Building2, Globe, Facebook, Instagram, Star,
  ChevronLeft, MapPin, MessageSquare, Image as ImageIcon, ShieldCheck, Loader2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { businessTypes } from '../mock';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';

const Onboarding = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const totalSteps = 4;

  const [businessName, setBusinessName] = useState(user?.businessName || '');
  const [businessType, setBusinessType] = useState('other');
  const [postcode, setPostcode] = useState('');
  const [connections, setConnections] = useState({
    google: false, facebook: false, instagram: false,
  });
  const [connectingProvider, setConnectingProvider] = useState(null);

  const fakeConnect = (provider) => {
    setConnectingProvider(provider);
    setTimeout(() => {
      setConnections(prev => ({ ...prev, [provider]: true }));
      setConnectingProvider(null);
      toast({ title: `${provider.charAt(0).toUpperCase() + provider.slice(1)} connected (demo)` });
    }, 900);
  };

  const finish = () => {
    toast({ title: 'You’re all set!', description: 'Let’s build your first ad.' });
    navigate('/create');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-rose-50 to-amber-50 flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-2xl">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-400 to-rose-500 flex items-center justify-center shadow-md">
            <Sparkles className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-bold text-slate-900">MyBusinessAdPro</span>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-8">
          {[1,2,3,4].map(n => (
            <div key={n} className={`flex-1 h-1.5 rounded-full transition-colors ${
              n <= step ? 'bg-gradient-to-r from-orange-400 to-rose-500' : 'bg-white border border-slate-200'
            }`} />
          ))}
        </div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
          Step {step} of {totalSteps}
        </div>

        <div className="bg-white rounded-3xl border border-slate-200 shadow-xl shadow-rose-100/50 p-8 lg:p-10">
          {step === 1 && (
            <div>
              <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Tell us about your business</h1>
              <p className="mt-2 text-slate-600">A few quick details so AI can write ads that sound like you.</p>
              <div className="mt-6 space-y-4">
                <div className="space-y-1.5">
                  <Label>Business name</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="e.g. Bloom &amp; Petal Florists" className="pl-10 h-11" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Business type</Label>
                  <Select value={businessType} onValueChange={setBusinessType}>
                    <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {businessTypes.map(b => <SelectItem key={b.id} value={b.id}>{b.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>UK postcode <span className="text-slate-400 font-normal">(for local targeting)</span></Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input value={postcode} onChange={(e) => setPostcode(e.target.value.toUpperCase())} placeholder="e.g. LS1 4DT" className="pl-10 h-11 uppercase" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Connect your accounts</h1>
              <p className="mt-2 text-slate-600">Link the places you want ads to live. You can skip and connect later.</p>
              <div className="mt-6 space-y-3">
                <ConnectRow
                  icon={<GoogleBadge />}
                  label="Google Business Profile"
                  desc="Manage listing, reviews, photos"
                  connected={connections.google}
                  loading={connectingProvider === 'google'}
                  onConnect={() => fakeConnect('google')}
                />
                <ConnectRow
                  icon={<Facebook className="w-5 h-5 text-[#1877F2]" />}
                  label="Facebook Page"
                  desc="Post ads &amp; manage budget via Meta"
                  connected={connections.facebook}
                  loading={connectingProvider === 'facebook'}
                  onConnect={() => fakeConnect('facebook')}
                />
                <ConnectRow
                  icon={<Instagram className="w-5 h-5 text-rose-500" />}
                  label="Instagram Business"
                  desc="Reels &amp; image ads to local audience"
                  connected={connections.instagram}
                  loading={connectingProvider === 'instagram'}
                  onConnect={() => fakeConnect('instagram')}
                />
              </div>
              <div className="mt-4 text-xs text-slate-500">
                <Globe className="w-3 h-3 inline mr-1" /> Demo connections — real OAuth flows can be enabled when you provide developer keys.
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">What can MyBusinessAdPro do?</h1>
              <p className="mt-2 text-slate-600">Quick tour before you create your first ad.</p>
              <div className="mt-6 space-y-3">
                {[
                  { icon: Sparkles, t: 'AI ad copy', d: 'Headlines, body, CTA, hashtags — in 30 seconds.' },
                  { icon: ImageIcon, t: 'AI ad imagery', d: 'Or upload your own product photos.' },
                  { icon: MessageSquare, t: 'AI review replies', d: 'Respond to Google/FB reviews like a pro.' },
                  { icon: ShieldCheck, t: 'UK ASA compliance', d: 'Ads checked against UK advertising law.' },
                ].map((f, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-orange-50/50 border border-orange-100">
                    <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center flex-shrink-0">
                      <f.icon className="w-5 h-5 text-orange-500" />
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">{f.t}</div>
                      <div className="text-sm text-slate-600">{f.d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-orange-400 to-rose-500 flex items-center justify-center shadow-lg">
                <Star className="w-8 h-8 text-white" strokeWidth={2.5} />
              </div>
              <h1 className="mt-5 text-3xl font-extrabold text-slate-900 tracking-tight">You’re ready, {user?.name?.split(' ')[0] || 'friend'}!</h1>
              <p className="mt-2 text-slate-600 max-w-md mx-auto">Let’s create your first scroll-stopping ad. Takes under 30 seconds.</p>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between gap-3">
            {step > 1 ? (
              <Button variant="ghost" onClick={() => setStep(step - 1)} className="text-slate-600">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            ) : <div />}
            {step < totalSteps ? (
              <Button onClick={() => setStep(step + 1)} className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl px-6 h-11">
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={finish} className="bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-400 hover:to-rose-400 text-white rounded-xl px-6 h-11 font-semibold">
                Create my first ad <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>

        <div className="mt-5 text-center">
          <button onClick={() => navigate('/dashboard')} className="text-sm text-slate-500 hover:text-slate-800">
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
};

const GoogleBadge = () => (
  <svg className="w-5 h-5" viewBox="0 0 48 48">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 19 13 24 13c3.1 0 5.8 1.2 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.4 0 10.3-2.1 14-5.4l-6.5-5.5c-2 1.4-4.6 2.3-7.5 2.3-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.6 16.3 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2.1-2.1 4-3.8 5.3l6.5 5.5C42.6 35 44 29.9 44 24c0-1.3-.1-2.7-.4-3.5z"/>
  </svg>
);

const ConnectRow = ({ icon, label, desc, connected, loading, onConnect }) => (
  <div className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 hover:border-slate-300 transition-colors">
    <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0">{icon}</div>
    <div className="flex-1">
      <div className="font-semibold text-slate-900">{label}</div>
      <div className="text-xs text-slate-500">{desc}</div>
    </div>
    {connected ? (
      <Badge className="bg-emerald-100 text-emerald-800 border-0"><Check className="w-3 h-3 mr-1" /> Connected</Badge>
    ) : (
      <Button size="sm" variant="outline" onClick={onConnect} disabled={loading} className="min-w-[90px]">
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Connect'}
      </Button>
    )}
  </div>
);

export default Onboarding;
